/**
 * =============================================================================
 * mp-free-gift-with-purchase
 * =============================================================================
 * Handle client-side of the FGWP mechanism.
 * 
 * See also:
 * 
 * - fgwp-modal.liquid (the FGWP modal)
 * - fgwp.liquid (the FGWP product page messaging)
 * - fgwp-cart-offcanvas.liquid (the FGWP minicart cart messaging)
 * - fgwp-line-item (the line item messaging once the FGWP has been added)
 * 
 * This extension also requires:
 * 
 * - a Shopify Script to ensure all thresholds have been met and the 
*    shipping location is domestic, and that only 1 FGWP line item is added.
 *
 * Cart JSON: gift lines may use _fgwp (theme modal) and/or _dk_gift (Discount Kit).
 * Eligible spend for the modal threshold excludes gift lines so £0 gifts do not
 * count toward “met threshold” the same way as Liquid messaging where applicable.
 * =============================================================================
 */

var FGWP = {
  data: {
    selectors: {
      threshold: "[data-mp-fgwp-threshold]",
      threshold_attribute: "data-mp-fgwp-threshold",

      product_ids: "[data-mp-fgwp-products]",
      product_ids_attribute: "data-mp-fgwp-products",

      to_spend_amount: ".mp-fgwp-to-spend-amount",
      to_spend_message: ".mp-fgwp-to-spend-message",

      add_buttons: ".mp-modal-fgwp-add",
      variant_select: ".mp-modal-fgwp-variant",

      met_threshold_message: ".mp-fgwp-threshold-met"
    },

    threshold: null,
    product_ids: [],
    user_country: null,
    included_countries: null,
    all_countries: true
  },

  isGiftProductId: function (productId) {
    return this.data.product_ids.some(function (id) {
      return String(id) === String(productId);
    });
  },

  lineHasProperty: function (item, propName) {
    var p = item.properties;
    if (p == null) {
      return false;
    }
    if (Array.isArray(p)) {
      return p.some(function (entry) {
        return entry && String(entry.name) === propName;
      });
    }
    return !!p[propName];
  },

  /**
   * True when this line is the free FGWP / DK gift (not the paid sellable line).
   */
  isGiftLine: function (item, cartItems) {
    if (item.gift_card === true) {
      return false;
    }
    if (!this.isGiftProductId(item.product_id)) {
      return cartItems.some(function (other) {
        return (
          other.key !== item.key &&
          other.product_id === item.product_id &&
          other.final_line_price > 0 &&
          item.final_line_price === 0
        );
      });
    }
    if (this.lineHasProperty(item, '_fgwp') || this.lineHasProperty(item, '_dk_gift')) {
      return true;
    }
    return item.final_line_price === 0;
  },

  cartHasFgwpGift: function (cartItems) {
    var that = this;
    if (!cartItems || !cartItems.length) {
      return false;
    }
    return cartItems.some(function (item) {
      return that.isGiftLine(item, cartItems);
    });
  },

  cartEligibleTotal: function (cartItems) {
    var that = this;
    if (!cartItems || !cartItems.length) {
      return 0;
    }
    return cartItems.reduce(function (sum, item) {
      if (item.gift_card === true) {
        return sum;
      }
      if (that.isGiftLine(item, cartItems)) {
        return sum;
      }
      return sum + item.final_line_price;
    }, 0);
  },

  /**
   * Setup the FGWP mechanism, listening for cart / minicart state, handling
   * FGWP modal interactions, etc.
   */
  onReady: function(){
    if (document.querySelectorAll('.mp-fgwp-container').length == 0) {
      return;
    }

    var thresholdEl = document.querySelector(this.data.selectors.threshold);
    var productsEl = document.querySelector(this.data.selectors.product_ids);
    var countriesEl = document.querySelector('[data-fgwp-included-countries]');
    if (!thresholdEl || !productsEl || !countriesEl) {
      return;
    }
    this.data.threshold = parseInt(thresholdEl.dataset.mpFgwpThreshold, 10);
    if (!Number.isFinite(this.data.threshold) || this.data.threshold <= 0) {
      return;
    }
    try {
      this.data.product_ids = JSON.parse(productsEl.dataset.mpFgwpProducts);
    } catch (err) {
      this.data.product_ids = [];
    }
    this.data.included_countries = countriesEl.dataset.fgwpIncludedCountries.split(',');

    if (this.data.all_countries) {
      this.setupOffer();
    } else {
      document.addEventListener('geodata:loaded', this.handleGeodataLoaded.bind(this));
    }

    const myModalEl = document.getElementById('mp-modal-fgwp');
    if (myModalEl) {
      myModalEl.addEventListener('hidden.bs.modal', function () {
        if (MP.Utilities.isProductPage()) {
          const backdrop = document.querySelector('.modal-backdrop.fade.show');
          if (backdrop) {
            backdrop.remove();
          }
        }
      });
    }
  },

  handleGeodataLoaded: function(event) {
    var geodataJSON = event.detail.data;
    this.data.user_country = geodataJSON.country.iso_code;

    // Activate if applicable to this country
    if(this.data.included_countries.includes(this.data.user_country)){
      this.setupOffer();
    } else {
      console.log("[FGWP] Ignore - no locale match");
    }
  },

  setupOffer: function() {
    var banner = document.querySelector('.mp-fgwp-banner-enabled');
    if (banner) {
      banner.classList.remove('d-none');
    }

    document.addEventListener('minicart:updated', this.handleCartUpdated.bind(this));
    document.addEventListener('cart:changed', this.handleCartUpdated.bind(this));

    // Handle events on the modal
    //$("[data-mp-fgwp-modal-option]").on("click", this.handleGiftVariantSelect.bind(this));
    document.querySelectorAll(this.data.selectors.add_buttons).forEach(element => {
      element.addEventListener("click", this.addFreeGift.bind(this));
    });
  },

  /**
   * Add the selected FGWP variant to the cart, if FR is found
   * @returns undefined
   */
  addFreeGift: function(e) {
    let closestTile = e.target.closest(".mp-modal-fgwp-tile");
    let selectedVariant = closestTile.querySelector(this.data.selectors.variant_select).value;

    if(selectedVariant == ""){
      return;
    }

    // Add it to the cart
    let productToAdd = {
      id: selectedVariant,
      quantity: 1,
      properties: { _fgwp: true, _dk_gift: ' ' }
    };
    MP.Extensions.Cart.add(productToAdd);

    // Hide the FGWP modal
    document.querySelector('#mp-modal-fgwp .btn-close').click();

    // Need to hide the second modal backdrop that gets added on the product page
    // when the FGWP modal is opened
    if(MP.Utilities.isProductPage()){
      const backdrop = document.querySelector(".modal-backdrop.fade.show");
      if (backdrop) {
          backdrop.remove();  // Removes the first matching element
      } 
    }
  },

  /**
   * Check the newly updated cart and update messaging / trigger the FGWP modal
   * if the threshold has been met.
   * 
   * @param {Event} e 
   * @returns undefined
   */
  handleCartUpdated: function(e) {
    var cart = e.detail && e.detail.cart;
    var cartItems = cart && cart.items;
    if (cartItems === undefined) {
      return;
    }

    var hasItem = this.cartHasFgwpGift(cartItems);
    var eligibleTotal = this.cartEligibleTotal(cartItems);

    if (!hasItem && eligibleTotal >= this.data.threshold) {
      this.triggerPrompt();
    }
  },


  /**
   * Show the FGWP modal unless we already showed it this journey (cookie === 'true').
   * Do not treat other cookie values as "already shown" (e.g. legacy boolean "false").
   *
   * @returns undefined
   */
  triggerPrompt: function() {
    var modalEl = document.getElementById('mp-modal-fgwp');
    if (!modalEl) {
      return;
    }
    var promptCookie =
      typeof Cookies !== 'undefined' ? Cookies.get('fgwp-prompt-shown') : undefined;
    if (promptCookie === 'true') {
      return;
    }
    var thatModal = modalEl;
    setTimeout(function () {
      if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        bootstrap.Modal.getOrCreateInstance(thatModal).show();
      } else {
        var opener =
          document.querySelector('#cart-offcanvas #fgwp-model-open') ||
          document.querySelector('#fgwp-model-open');
        if (opener) {
          opener.click();
        }
      }
      if (typeof Cookies !== 'undefined') {
        Cookies.set('fgwp-prompt-shown', 'true');
      }
    }, 500);
  }
};

export default FGWP;